import { useCallback } from 'react';
import { connect } from 'react-redux';

import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef, getTargetEntitySelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  getActAddressBlockId,
  getActInnLabelBlockId,
  getActOgrnLabelBlockId,
  getActOrganizationNameBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { ErknmSubjectAdd } from 'KnmPmPackage/PmResultOfInspection/components/PmAct/PmActItem/components/PmActSubject/components/PmSubjectInfo';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { KnmSubject } from 'KnmPackage/KnmSubjectModule/components/KnmSubject';
import { resultDecisionSubjectTypeOptions } from 'KnmPmPackage/PmResultOfInspection/components/PmResultOfInspectionPmResultDecisions';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';

import styles from './ResultDecisionsPmItemContentSubject.module.scss';

interface IResultDecisionsPmItemContentSubjectWithFounderProps {
  id: string;
  name: string;
  value: IKnmPmFormResultDecision;
  lateFieldName: string;
}

interface IResultDecisionsPmItemContentSubjectWithFounderStateProps {
  formRef: IKnmFormRef | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const ResultDecisionsPmItemContentSubjectWithFounderWithoutStore = ({
  id,
  name,
  value,
  formRef,
  subjectTypeOptions,
  lateFieldName,
}: IResultDecisionsPmItemContentSubjectWithFounderProps &
  IResultDecisionsPmItemContentSubjectWithFounderStateProps) => {
  const isControlledPerson = value.subjectType?.value === KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT;

  const getResultDecisionsFounderFieldBlockId = useCallback((fieldId: string) => `${name}.founder.${fieldId}`, [name]);

  const isUR = value.organization?.subjectTypePm?.label === KnmSubjectTypesEnum.UR;

  return (
    <>
      <Row>
        <FieldBlock
          label="Лицо, в отношении которого принято решение"
          size="half"
          id={`${id}.subjectType`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/subjectType`} fieldId={`${id}.subjectType`} />}
        >
          <SelectInputField
            name={`${name}.subjectType`}
            disabled={value.signed || !isUR}
            isClearable={false}
            options={resultDecisionSubjectTypeOptions}
          />
        </FieldBlock>
      </Row>
      <KnmCollapse
        id={`${id}.organizationBlock`}
        theme="fields"
        title={isControlledPerson ? 'Сведения о контролируемом лице' : 'Сведения об учредителе'}
        openByDefault
      >
        {isControlledPerson ? (
          <div className={styles.Wrapper}>
            <FieldBlock label={'Сведения о контролируемом лице'} />
            <div className={styles.SubjectAdd}>
              <ErknmSubjectAdd
                id={id}
                name={`${name}.organization`}
                inn={value.organization?.inn}
                innLabelBlockId={getActInnLabelBlockId(id)}
                ogrn={value.organization?.ogrn}
                ogrnLabelBlockId={getActOgrnLabelBlockId(id)}
                organizationNameBlockId={getActOrganizationNameBlockId(id)}
                addressBlockId={getActAddressBlockId(id)}
                checkSubjectResult={value.organization?.checkSubjectResult || null}
                subjectType={value.organization?.subjectTypePm}
                snils={value.organization?.snils}
                ervkId={value.organization?.ervkId}
                inspectionResults={value.organization?.inspectionResults}
              />
            </div>
          </div>
        ) : (
          <KnmSubject
            formRef={formRef}
            watchOnly={!!value.signed}
            inn={value.founder?.inn}
            ogrn={value.founder?.ogrn}
            snils={value.founder?.snils}
            isFiz={value.founder?.isFiz}
            rafpNza={value.founder?.rafpNza}
            kpp={value.founder?.kpp}
            isSonko={value.founder?.isSonko}
            subjectType={value.founder?.subjectTypePm}
            organizationAutocomplete={value.founder?.organizationAutocomplete}
            checkSubjectResult={value.founder?.checkSubjectResult}
            smevRequestStatus={value.founder?.smevRequestStatus}
            name={`${name}.founder`}
            subjectTypeLabel="Тип учредителя"
            mspCode={value.founder?.mspCode}
            mspRegistryDate={value.founder?.mspRegistryDate}
            getBlockId={getResultDecisionsFounderFieldBlockId}
            subjectTypeOptions={subjectTypeOptions}
            isIt={value.founder?.isPresentInItRegistry}
            itAccreditationResponse={value.founder?.itAccreditationResponse}
            ervkId={value.founder?.ervkId}
          />
        )}
      </KnmCollapse>
    </>
  );
};

const mapStateToProps = (state: IRootState): IResultDecisionsPmItemContentSubjectWithFounderStateProps => ({
  formRef: getKnmFormRef(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
});

export const ResultDecisionsPmItemContentSubjectWithFounder = connect(mapStateToProps)(
  ResultDecisionsPmItemContentSubjectWithFounderWithoutStore,
);
